'use client';

import { Modal } from './Modal';
import { useLanguage } from '@/context/LanguageContext';

interface BulkEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  subject: string;
  onSubjectChange: (value: string) => void;
  message: string;
  onMessageChange: (value: string) => void;
  sending: boolean;
  onSend: () => void;
}

export function BulkEmailModal({
  isOpen,
  onClose,
  selectedCount,
  subject,
  onSubjectChange,
  message,
  onMessageChange,
  sending,
  onSend,
}: BulkEmailModalProps) {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t.groupTabs.bulkEmailModalTitle}
      subtitle={`${t.groupTabs.bulkEmailModalSubtitle} (${selectedCount} ${t.groupTabs.selectedItems})`}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-background-secondary disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="bulk-email-modal-cancel-btn"
          >
            {t.groupTabs.cancel}
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={!subject.trim() || !message.trim() || sending}
            className="px-4 py-2 text-sm bg-primary text-background rounded-md hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="bulk-email-modal-send-btn"
          >
            {sending ? t.groupTabs.bulkEmailSending : t.groupTabs.bulkEmailSend}
          </button>
        </>
      }
    >
      <div className="space-y-4" data-testid="bulk-email-modal-root">
        <span data-testid="bulk-email-modal-recipient-count" className="sr-only">{selectedCount}</span>
        <div>
          <label htmlFor="bulk-email-subject" className="block text-sm font-medium text-foreground mb-1.5">
            {t.groupTabs.bulkEmailSubject} *
          </label>
          <input
            id="bulk-email-subject"
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder={t.groupTabs.bulkEmailSubjectPlaceholder}
            className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="bulk-email-modal-subject"
          />
        </div>
        <div>
          <label htmlFor="bulk-email-message" className="block text-sm font-medium text-foreground mb-1.5">
            {t.groupTabs.bulkEmailMessage} *
          </label>
          <textarea
            id="bulk-email-message"
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder={t.groupTabs.bulkEmailMessagePlaceholder}
            rows={6}
            className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            data-testid="bulk-email-modal-message"
          />
        </div>
      </div>
    </Modal>
  );
}
